import { Dialog } from "radix-ui";
import DragIndicatorIcon from "../../icons/DragIndicatorIcon";
import DeleteIcon from "../../icons/DeleteIcon";
import { Reorder } from "motion/react";
import { del } from "../../../api/util";
import { FilterPreset } from "../../../interfaces/filters";
import Button from "@/components/bricks/button/Button";
import { useContext, useEffect, useState } from "react";
import { FilterPresetsEventContext } from "../../../context/FiltersContext";
import { DEFAULT_ERROR } from "../../../constants/errors";
import useLocalStorageState from "use-local-storage-state";
import { useConfirm } from "../../confirm/useConfirm";
import { UserQueryContext } from "../../../context/UserContext";

const FilterPresetsDropdownReorder = (props: {
  container: HTMLElement | null;
  contextType: FilterPreset["type"];
  contextFilters: FilterPreset[];
}) => {
  // props
  const { container, contextType, contextFilters } = props;

  // context
  const filterPresetsEvent = useContext(FilterPresetsEventContext);
  const { user } = useContext(UserQueryContext);

  // state
  const [changed, setChanged] = useState(false);
  const [tmpFilters, setTmpFilters] = useState<FilterPreset[]>(contextFilters);
  const [, setOrder] = useLocalStorageState<string[]>(
    `${contextType}FilterOrder`,
  );

  // usage
  const { confirmDialog } = useConfirm();

  // effects
  useEffect(() => {
    setTmpFilters(contextFilters);
  }, [contextFilters]);

  // takin' care of business
  const myFilters = tmpFilters.filter((filterObj) => filterObj.erPersonlig);
  const standardFilters = tmpFilters.filter(
    (filterObj) => !filterObj.erPersonlig,
  );

  // handlers
  function reOrderPresetsHandler(filterPresets: FilterPreset[]) {
    setOrder(filterPresets.map((preset) => preset.id));
    filterPresetsEvent({
      event: "set",
      filters: filterPresets,
    });
    setChanged(false);
  }

  async function deleteFilterHandler(filter: FilterPreset) {
    const c = await confirmDialog(
      "Slet gemt filter",
      "Du er ved at slette et gemt filter, dette kan ikke fortrydes. Vil du forsætte?",
      "Nej, gå tilbage",
      "Ja, slet filter",
    );

    if (!c) {
      return;
    }

    try {
      del({
        query: `brugere/${user?.id}/soegefiltre/${filter.id}`,
      });

      filterPresetsEvent({
        event: "delete",
        ids: [filter.id],
      });

      setTmpFilters((latest) =>
        latest.filter((preset) => preset.id !== filter.id),
      );
    } catch (error) {
      console.error(error, DEFAULT_ERROR);
    }
  }

  return (
    <Dialog.Portal container={container}>
      <Dialog.Overlay className="c-modal-overlay c-modal-overlay--sidebar">
        <Dialog.Content className="c-modal c-modal--sidebar p-2">
          <div className="c-dropdown-menu-label">Mine filtre</div>
          {myFilters && myFilters.length > 0 ? (
            <Reorder.Group
              axis="y"
              values={myFilters}
              onReorder={(value: FilterPreset[]) => {
                setTmpFilters([...standardFilters, ...value]);
                setChanged(true);
              }}
              as="ul"
              layoutScroll
              {...{ className: "reorder-group p-0 d-flex flex-column gap-1" }}
            >
              {myFilters.map((filter) => (
                <Reorder.Item
                  value={filter}
                  as={"li"}
                  className={`c-dropdown-menu-item c-dropdown-menu-item--editmode ${
                    filter.del ? "text-decoration-line-through" : ""
                  }`}
                  style={{ cursor: "grab" }}
                  key={`myFilters-reorder-${filter.navn}`}
                >
                  <DragIndicatorIcon /> {filter.navn}{" "}
                  <button
                    aria-label="Slet filter"
                    className="btn btn-secondary btn--transparent btn--icon ms-auto tooltip-end"
                    onClick={() => {
                      deleteFilterHandler(filter);
                    }}
                  >
                    <DeleteIcon />
                  </button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div
              className="c-dropdown-menu-item c-dropdown-menu-item--editmode text-muted lh-lg"
              data-disabled
            >
              <i>Ingen gemte filtre</i>
            </div>
          )}
          <div
            role="separator"
            aria-orientation="horizontal"
            className="c-dropdown-menu-separator"
          ></div>
          <div className="c-dropdown-menu-label">Standardfiltre</div>
          {standardFilters && standardFilters.length > 0 ? (
            <Reorder.Group
              axis="y"
              values={standardFilters}
              onReorder={(value: FilterPreset[]) => {
                setTmpFilters([...myFilters, ...value]);
                setChanged(true);
              }}
              as="ul"
              layoutScroll
              {...{ className: "reorder-group p-0 d-flex flex-column gap-1" }}
            >
              {standardFilters.map((filter) => (
                <Reorder.Item
                  value={filter}
                  as={"li"}
                  className={`c-dropdown-menu-item c-dropdown-menu-item--editmode ${
                    filter.del ? "text-decoration-line-through" : ""
                  }`}
                  style={{ cursor: "grab" }}
                  key={`myFilters-reorder-${filter.navn}`}
                >
                  <DragIndicatorIcon /> {filter.navn}{" "}
                  <button
                    aria-label="Slet filter"
                    className="btn btn-secondary btn--transparent btn--icon ms-auto tooltip-end"
                    onClick={() => {
                      deleteFilterHandler(filter);
                    }}
                  >
                    <DeleteIcon />
                  </button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div
              className="c-dropdown-menu-item c-dropdown-menu-item--editmode text-muted lh-lg"
              data-disabled
            >
              <i>Ingen gemte filtre</i>
            </div>
          )}
          <div
            role="separator"
            aria-orientation="horizontal"
            className="c-dropdown-menu-separator"
          ></div>
          <div className="p-1 d-flex justify-content-between gap-2 mt-auto">
            <Dialog.Close asChild>
              <Button color="secondary">Annuller</Button>
            </Dialog.Close>
            <Dialog.Close
              asChild
              disabled={!changed}
              onClick={() => {
                if (changed) {
                  reOrderPresetsHandler(tmpFilters);
                }
              }}
            >
              <Button color="primary">Gem</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Portal>
  );
};

export default FilterPresetsDropdownReorder;
